export const DEFAULT_AGENDA_MINUTES = 60;
export const MAX_AGENDA_MINUTES_TOTAL = 60;
export const TWO_ROW_SPLIT = [45, 15];

/**
 * @typedef {{ minutes: number | string, focus: string }} AgendaRow
 */

/**
 * @param {{ focus?: string }} [options]
 * @returns {AgendaRow[]}
 */
export function createDefaultAgendaRows({ focus = '' } = {}) {
  return [{ minutes: DEFAULT_AGENDA_MINUTES, focus: focus || '' }];
}

/**
 * @param {AgendaRow[]} rows
 * @returns {number}
 */
export function getAgendaTotalMinutes(rows) {
  return rows.reduce((sum, row) => sum + (Number(row.minutes) || 0), 0);
}

/**
 * @param {AgendaRow[]} rows
 * @param {{ requireFocus?: boolean }} [options]
 * @returns {{
 *   valid: boolean;
 *   emptyFocusIndices: number[];
 *   invalidMinutesIndices: number[];
 *   totalExceeds: boolean;
 * }}
 */
export function getAgendaValidation(rows, { requireFocus = true } = {}) {
  const emptyFocusIndices = [];
  const invalidMinutesIndices = [];

  if (!rows?.length) {
    return { valid: false, emptyFocusIndices: [], invalidMinutesIndices: [0], totalExceeds: false };
  }

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const minutes = Number(row.minutes);
    if (!Number.isFinite(minutes) || minutes < 1) {
      invalidMinutesIndices.push(i);
    }
    if (requireFocus && !String(row.focus || '').trim()) {
      emptyFocusIndices.push(i);
    }
  }

  const total = getAgendaTotalMinutes(rows);
  const totalExceeds = total > MAX_AGENDA_MINUTES_TOTAL;

  return {
    valid:
      emptyFocusIndices.length === 0 &&
      invalidMinutesIndices.length === 0 &&
      !totalExceeds,
    emptyFocusIndices,
    invalidMinutesIndices,
    totalExceeds,
  };
}

/** @deprecated Use getAgendaValidation — returns message only when blocks submit from hook */
export function validateAgendaRows(rows, options = {}) {
  const v = getAgendaValidation(rows, options);
  if (v.valid) return null;
  if (v.totalExceeds) return 'Total time cannot exceed 60 minutes.';
  if (v.invalidMinutesIndices.length) return 'invalid_minutes';
  if (v.emptyFocusIndices.length) return 'empty_focus';
  return 'invalid';
}

/**
 * @param {AgendaRow[]} rows
 * @param {number} index
 * @param {number|string} nextMinutes
 * @returns {AgendaRow[]}
 */
export function applyMinutesChange(rows, index, nextMinutes) {
  const parsed = parseInt(String(nextMinutes), 10);
  const raw = Number.isFinite(parsed) ? parsed : '';
  const otherTotal = rows.reduce(
    (sum, row, i) => (i === index ? sum : sum + (Number(row.minutes) || 0)),
    0
  );
  const maxForRow =
    raw === '' ? MAX_AGENDA_MINUTES_TOTAL : Math.max(1, MAX_AGENDA_MINUTES_TOTAL - otherTotal);
  const clamped =
    raw === '' ? '' : Math.min(Math.max(1, raw), maxForRow);

  return rows.map((row, i) => (i === index ? { ...row, minutes: clamped } : row));
}

/**
 * @param {AgendaRow[]} rows
 * @returns {AgendaRow[]}
 */
export function addAgendaRow(rows) {
  if (rows.length === 0) {
    return createDefaultAgendaRows();
  }
  if (rows.length === 1) {
    return [
      { ...rows[0], minutes: TWO_ROW_SPLIT[0] },
      { minutes: TWO_ROW_SPLIT[1], focus: '' },
    ];
  }

  const total = getAgendaTotalMinutes(rows);
  const remaining = Math.max(1, MAX_AGENDA_MINUTES_TOTAL - total);
  if (remaining <= 0) {
    const next = [...rows];
    const donorIdx = next.reduce(
      (best, row, i) =>
        (Number(row.minutes) || 0) > (Number(next[best].minutes) || 0) ? i : best,
      0
    );
    const donorMinutes = Number(next[donorIdx].minutes) || 0;
    if (donorMinutes <= 1) {
      return next;
    }
    next[donorIdx] = { ...next[donorIdx], minutes: donorMinutes - 1 };
    return [...next, { minutes: 1, focus: '' }];
  }

  return [...rows, { minutes: remaining, focus: '' }];
}

/**
 * @param {AgendaRow[]} rows
 * @returns {string}
 */
export function formatAgendaForCalendly(rows) {
  return rows
    .map((row) => {
      const minutes = Math.max(1, Math.round(Number(row.minutes) || 1));
      const focus = String(row.focus || '').trim();
      return `${minutes} min — ${focus}`;
    })
    .join('\n');
}

/**
 * @param {AgendaRow[]} rows
 * @returns {boolean}
 */
export function agendaMinutesDifferFromDefault(rows) {
  const total = getAgendaTotalMinutes(rows);
  return rows.length > 1 || total !== DEFAULT_AGENDA_MINUTES;
}
